/**
 * Derives the restart-gating snapshot from a run-detail payload: whether the run
 * has reached the enrichment stage (a coordinator exists, so the restart cursor
 * is resumable) and how many curated products are still to enrich. Mirrors the
 * backend contract's `Remaining = curatedTotal − cumulativeProducts`.
 */
import type { EnrichmentRunProgress } from '../types/workflow-control.types';

/** Pipeline stage order — a decoupled copy of workflow-tracking's `STAGES`, kept
 *  local so content-enrichment doesn't reach into that feature. Used only to test
 *  "is the run at ENRICHMENT or later". */
const STAGE_ORDER = [
  'IMPORT',
  'IMAGE_GATE',
  'CURATION',
  'FETCH',
  'PRE_ENRICH_SCORE',
  'SPLIT',
  'SAMPLE_SELECT',
  'SAMPLE',
  'ARM_ASSIGNMENT',
  'ENRICHMENT',
  'POST_ENRICH_SCORE',
  'ROLLUPS',
  'ENRICHMENT_INSIGHTS',
  'APPROVAL',
  'DELIVERY',
  'EXPORT_PLAN',
  'EXPORT_WRITE',
  'EXPORT_COMPOSE',
  'TERMINAL',
] as const;

const ENRICHMENT_INDEX = STAGE_ORDER.indexOf('ENRICHMENT');

/** Minimal projection of the run-detail payload the restart gate reads. Mirrors
 *  (does not import) the workflow-tracking `RunDetail` shape to stay decoupled. */
export interface RestartRunDetail {
  summary: { currentStage: string | null };
  stages: {
    stage: string | null;
    stageName: string;
    productsExpected: number;
    productsSucceeded: number;
  }[];
}

/** Compute the restart-gating snapshot from run detail. `curatedTotal`/`remaining`
 *  come from the ENRICHMENT stage's counters (expected ≈ curated total, succeeded
 *  ≈ already done); both are `0` until that stage declares its denominator. */
export function deriveRestartProgress(detail: RestartRunDetail): EnrichmentRunProgress {
  const { currentStage } = detail.summary;
  const currentIndex = currentStage
    ? STAGE_ORDER.indexOf(currentStage as (typeof STAGE_ORDER)[number])
    : -1;
  const isEnrichmentOrLater = currentIndex >= ENRICHMENT_INDEX;

  const enrichmentStage = detail.stages.find(
    (s) => s.stage === 'ENRICHMENT' || s.stageName === 'ENRICHMENT',
  );
  const curatedTotal = enrichmentStage?.productsExpected ?? 0;
  const done = enrichmentStage?.productsSucceeded ?? 0;
  const remaining = Math.max(0, curatedTotal - done);

  return { isEnrichmentOrLater, curatedTotal, remaining };
}
